import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { useAuth } from './hooks/useAuth';
import { LoginPage } from './components/LoginPage';
import { RegisterPage } from './components/RegisterPage';
import { WelcomePage } from './components/WelcomePage';
import { SimulationBrowser } from './components/SimulationBrowser';
import { AdminPanelPage } from './components/AdminPanelPage';
import { GlobalHeader } from './components/GlobalHeader';
import { UserMenu } from './components/UserMenu';
import { SimMenu } from './components/SimMenu';
import { OrganismPanel } from './components/OrganismPanel';
import { SnapshotBrowser } from './components/SnapshotBrowser';
import DefaultSimRenderer from './components/DefaultSimRenderer';
import { getSimulation, getSimulationArtifact, getSimulationConfig, getAuthToken } from './services/api';
import type { Simulation, SimulationRendererProps, GenesisConfig } from './types/simulation';
import './App.css';

type AppView = 'welcome' | 'browser' | 'simulation' | 'admin';

function SimulationView({ simulation: initialSimulation, onNavigateToAdmin }: {
  simulation: Simulation;
  onBack: () => void;
  onNavigateToAdmin: () => void;
}) {
  const { showNotification } = useNotification();
  const [simulation, setSimulation] = useState(initialSimulation);
  const [RendererComponent, setRendererComponent] = useState<React.ComponentType<SimulationRendererProps> | null>(null);
  const [rendererLoading, setRendererLoading] = useState(false);
  const [simConfig, setSimConfig] = useState<GenesisConfig | undefined>(undefined);
  const [selectedEntityId, setSelectedEntityId] = useState<string | undefined>(undefined);
  const [hoveredEntityId, setHoveredEntityId] = useState<string | undefined>(undefined);
  const [snapToEntityId, setSnapToEntityId] = useState<string | undefined>(undefined);
  const [dismissSignal, setDismissSignal] = useState(0);
  const [devMode, setDevMode] = useState(true);
  const [mobileTab, setMobileTab] = useState<'organisms' | 'snapshots' | null>(null);

  useEffect(() => {
    if (dismissSignal > 0) setMobileTab(null);
  }, [dismissSignal]);

  useEffect(() => {
    getSimulation(simulation.id)
      .then(setSimulation)
      .catch(console.error);
  }, [simulation.id]);

  useEffect(() => {
    getSimulationConfig(simulation.id)
      .then(setSimConfig)
      .catch(console.error);
  }, [simulation.id]);

  useEffect(() => {
    let cancelled = false;
    setRendererLoading(true);
    getSimulationArtifact(simulation.id)
      .then(artifact => {
        if (cancelled) return;
        const url = artifact.renderer_url;
        if (!url) { setRendererComponent(null); setRendererLoading(false); return; }
        return import(/* @vite-ignore */ url)
          .then(mod => { if (!cancelled) setRendererComponent(() => mod.default); });
      })
      .catch(err => { if (!cancelled) console.error('Failed to load renderer:', err); })
      .finally(() => { if (!cancelled) setRendererLoading(false); });
    return () => { cancelled = true; };
  }, [simulation.id]);

  const broadcastUrl = useMemo(() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/simulations/${simulation.id}/broadcast/ws?token=${getAuthToken() ?? ''}`;
  }, [simulation.id]);

  const handleCopyLink = () => {
    const link = simulation.sharing_link || (() => {
      const url = new URL(window.location.origin);
      url.searchParams.set('sim', simulation.id);
      return url.toString();
    })();
    navigator.clipboard.writeText(link);
    showNotification('Link copied to clipboard!');
  };

  const handleCopySimId = () => {
    navigator.clipboard.writeText(simulation.id);
    showNotification('Simulation ID copied!');
  };

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalTarget(document.getElementById('global-toolbar-portal'));
  }, []);

  const toolbarContent = portalTarget ? ReactDOM.createPortal(
    <>
      <div className="toolbar-right">
        <SimMenu simulationId={simulation.id} devMode={devMode} onDevModeChange={setDevMode} onCopyId={handleCopySimId} onCopyLink={handleCopyLink} />
      </div>
    </>,
    portalTarget
  ) : null;

  const ActiveRenderer = RendererComponent ?? DefaultSimRenderer;

  return (
    <div className="app">
      <GlobalHeader title={simulation.name || simulation.id}>
        <UserMenu onNavigateToAdmin={onNavigateToAdmin} />
      </GlobalHeader>
      {toolbarContent}

      <div className="app-layout">
        <div className={`sidebar sidebar-left${mobileTab ? ' sidebar--tab-open' : ''}`}>
          {/* Desktop: both panels, normal layout */}
          <div className="sidebar-desktop">
            <OrganismPanel
              simulationId={simulation.id}
              devMode={devMode}
              selectedEntityId={selectedEntityId}
              hoveredEntityId={hoveredEntityId}
              snapToEntityId={snapToEntityId}
              dismissSignal={dismissSignal}
              onOrganismSelect={setSelectedEntityId}
              onOrganismHover={setHoveredEntityId}
              onSnapTo={setSnapToEntityId}
            />
            <SnapshotBrowser simulationId={simulation.id} />
          </div>

          {/* Mobile: active tab content */}
          <div className="mobile-tab-content">
            {mobileTab === 'organisms' && (
              <OrganismPanel
                simulationId={simulation.id}
                devMode={devMode}
                selectedEntityId={selectedEntityId}
                hoveredEntityId={hoveredEntityId}
                snapToEntityId={snapToEntityId}
                dismissSignal={dismissSignal}
                onOrganismSelect={setSelectedEntityId}
                onOrganismHover={setHoveredEntityId}
                onSnapTo={setSnapToEntityId}
                tabMode
              />
            )}
            {mobileTab === 'snapshots' && (
              <SnapshotBrowser simulationId={simulation.id} tabMode />
            )}
          </div>

          {/* Mobile: tab bar */}
          <div className="mobile-tab-bar">
            <button
              className={`mobile-tab${mobileTab === 'organisms' ? ' mobile-tab--active' : ''}`}
              onClick={() => setMobileTab(t => t === 'organisms' ? null : 'organisms')}
            >
              ORGANISMS
            </button>
            <button
              className={`mobile-tab${mobileTab === 'snapshots' ? ' mobile-tab--active' : ''}`}
              onClick={() => setMobileTab(t => t === 'snapshots' ? null : 'snapshots')}
            >
              SNAPSHOTS
            </button>
          </div>
        </div>

        <div className="simulation-broadcast-view" onClick={() => setDismissSignal(s => s + 1)}>
          {rendererLoading ? (
            <div className="broadcast-offline">
              <p>LOADING RENDERER</p>
            </div>
          ) : (
            <ActiveRenderer
              simulation={simulation}
              broadcastUrl={broadcastUrl}
              config={simConfig}
              selectedEntityId={selectedEntityId}
              hoveredEntityId={hoveredEntityId}
              snapToEntityId={snapToEntityId}
              onOrganismClick={(id) => { setSelectedEntityId(id); setSnapToEntityId(id); }}
              onOrganismHover={setHoveredEntityId}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Auth gate component
function AuthGate({
  children,
  authView,
  onSwitchView,
  onAuthSuccess,
}: {
  children: React.ReactNode;
  authView: 'login' | 'register';
  onSwitchView: (view: 'login' | 'register') => void;
  onAuthSuccess: () => void;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const [wasUnauthenticated, setWasUnauthenticated] = useState(false);

  // Track if user was unauthenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setWasUnauthenticated(true);
    }
  }, [isAuthenticated, isLoading]);

  // Call onAuthSuccess when user becomes authenticated after being unauthenticated
  useEffect(() => {
    if (isAuthenticated && wasUnauthenticated) {
      onAuthSuccess();
    }
  }, [isAuthenticated, wasUnauthenticated, onAuthSuccess]);

  // Block content until auth check completes
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>AUTHENTICATING...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authView === 'register') {
      return <RegisterPage onSwitchToLogin={() => onSwitchView('login')} />;
    }
    return <LoginPage onSwitchToRegister={() => onSwitchView('register')} />;
  }

  return <>{children}</>;
}

function App() {
  const [authView, setAuthView] = useState<'login' | 'register'>('login');
  const [currentView, setCurrentView] = useState<AppView>(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId) return 'simulation';

    // Check if user has completed onboarding
    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
    if (hasCompletedOnboarding) {
      return 'browser';
    }

    return 'welcome';
  });
  const [selectedSimulation, setSelectedSimulation] = useState<Simulation | null>(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId) {
      // Mark that we need to load this simulation
      return { id: simId, name: '', description: '' } as Simulation;
    }

    return null;
  });
  // Load saved theme on app mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'theme-echodigital';
    document.body.className = savedTheme;
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Read state from URL and history state
      const urlParams = new URLSearchParams(window.location.search);
      const simId = urlParams.get('sim');

      if (simId) {
        // Navigating forward to a simulation
        setSelectedSimulation({ id: simId, name: '', description: '' } as Simulation);
        setCurrentView('simulation');
      } else {
        // Navigating back from simulation - restore the previous view
        setSelectedSimulation(null);
        // Look for previousView in the next history entry (the simulation we just left)
        const view = event.state?.view || event.state?.previousView;
        if (view && view !== 'simulation') {
          setCurrentView(view);
        } else {
          // Fallback if no state
          const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
          setCurrentView(hasCompletedOnboarding ? 'browser' : 'welcome');
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToView = (view: AppView) => {
    setCurrentView(view);
    // Push state to browser history to enable back button
    const url = new URL(window.location.href);
    url.searchParams.delete('sim');
    window.history.pushState({ view }, '', url.toString());
  };

  const handleGetStarted = () => {
    localStorage.setItem('hasCompletedOnboarding', 'true');
    navigateToView('browser');
  };

  const handleNavigateToBrowser = () => {
    setSelectedSimulation(null);
    navigateToView('browser');
  };

  const handleNavigateToAdmin = () => {
    setSelectedSimulation(null);
    navigateToView('admin');
  };

  const handleSelectSimulation = (simulation: Simulation) => {
    setSelectedSimulation(simulation);
    // Update URL with simulation ID and store current view in history state
    const url = new URL(window.location.href);
    url.searchParams.set('sim', simulation.id);
    window.history.pushState({ view: 'simulation', previousView: currentView }, '', url.toString());
    setCurrentView('simulation');
  };

  const handleAuthSuccess = () => {
    // After auth, check if there's a simulation in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const simId = urlParams.get('sim');
    if (simId && (!selectedSimulation || selectedSimulation.id !== simId)) {
      // Reload simulation from URL
      setSelectedSimulation({ id: simId, name: '', description: '' } as Simulation);
      setCurrentView('simulation');
    }
  };

  const [headerTitle, setHeaderTitle] = useState('SIMULATION BROWSER');

  const renderView = () => {
    if (currentView === 'welcome') {
      return <WelcomePage onGetStarted={handleGetStarted} />;
    }

    if (currentView === 'simulation' && selectedSimulation) {
      return (
        <SimulationView
          simulation={selectedSimulation}
          onBack={() => window.history.back()}
          onNavigateToAdmin={handleNavigateToAdmin}
        />
      );
    }

    // All other views share the same shell with GlobalHeader
    return (
      <>
        <GlobalHeader title={headerTitle}>
          <UserMenu onNavigateToAdmin={handleNavigateToAdmin} />
        </GlobalHeader>

        {currentView === 'browser' && (
          <SimulationBrowser
            onSelectSimulation={handleSelectSimulation}
            setTitle={setHeaderTitle}
          />
        )}

        {currentView === 'admin' && (
          <AdminPanelPage
            onBack={handleNavigateToBrowser}
            setTitle={setHeaderTitle}
          />
        )}
      </>
    );
  };

  return (
    <AuthProvider>
      <NotificationProvider>
        <AuthGate authView={authView} onSwitchView={setAuthView} onAuthSuccess={handleAuthSuccess}>
          {renderView()}
        </AuthGate>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
